import { RouterStore } from '@estee/elc-router';

import {
    alreadyRegisteredPleaseSignInMessage,
    existingAccountFoundPleaseSignIn,
    existingAccountFoundWithMobilePleaseSignIn,
    IamType,
    MessageType,
    signInToConnectSocial
} from '~constants/Constants';
import { translationKeyMap } from '~constants/TranslationKeyMap';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { IMessage, MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { UserRepository } from '~repositories/UserRepository';
import { ACCOUNT_SIGN_IN_ROUTE } from '~routes/routes';
import { UserStateResolverService } from '~services/UserStateResolverService';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';

// Prepare values, messages and redirect to /sign-in page
export class RedirectedToSignInPageUseCase {
    private userStateResolver: UserStateResolverService;
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;
    private messageRepository: MessageRepository;
    private routerStore: RouterStore;

    constructor() {
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.configRepository = diContainer.get(serviceNames.configRepository);
        this.routerStore = diContainer.get(serviceNames.routerStore);
        this.messageRepository = diContainer.get(serviceNames.messageRepository);
        this.userStateResolver = new UserStateResolverService(
            this.userRepository.matchedExistingUsers
        );
    }

    execute = () => {
        const {
            accountSignInFormStore,
            consentForm: { isConsentFlow, isSocialSignUpFlow }
        } = this.userRepository;
        const { iamType, hasLoyalty } = this.configRepository;
        accountSignInFormStore.setValues({
            accountIdentifier: this.userStateResolver.signInAccountIdentifier,
            password: ''
        });

        if (isConsentFlow || isSocialSignUpFlow) {
            this.setMessages(
                [{ transKey: this.signInFormMessage, msgType: MessageType.INFO }],
                MessageGroup.SIGN_IN
            );
        } else if (iamType !== IamType.IN_STORE) {
            this.setMessages(
                [{ transKey: alreadyRegisteredPleaseSignInMessage }],
                MessageGroup.SIGN_IN
            );
        } else {
            const messageKey =
                hasLoyalty && this.userStateResolver.hasNonLoyaltyCompleteUser()
                    ? translationKeyMap.signInAndEnrollToLoyalty
                    : translationKeyMap.signInToContinue;
            this.setMessages(
                [
                    {
                        transKey: messageKey,
                        msgType: MessageType.INFO
                    }
                ],
                MessageGroup.IN_STORE_SIGN_IN_TO_CONTINUE
            );
        }

        // redirect to /sign-in
        // TODO: APACRIGEL-2278 fix routing
        this.routerStore.changeCurrentRoute(ACCOUNT_SIGN_IN_ROUTE);
        history.pushState(null, ACCOUNT_SIGN_IN_ROUTE);
    };

    private setMessages = (
        messages: IMessage[],
        messageGroup: MessageGroup = MessageGroup.SIGN_UP
    ) => this.messageRepository.setMessages(messageGroup, messages);

    // To determine the informative message on sign-in form when user is redirected from consent form
    private get signInFormMessage() {
        const {
            consentForm: { isSocialSignUpFlow }
        } = this.userRepository;
        if (!isSocialSignUpFlow) {
            return signInToConnectSocial;
        } else {
            // social-sign-up flow need special message other than normal social sign-in flow
            return this.userStateResolver.matchedCompleteUserByMobileOnly
                ? existingAccountFoundWithMobilePleaseSignIn
                : existingAccountFoundPleaseSignIn;
        }
    }
}
